import { resolveMessageFieldUid } from '@/domains/widgets/device-status-model';
import { WsEventType, type WsBaseMessage } from '@/shared/realtime/ws-types';

export interface BoardDeviceRealtimeEvent {
  correlationId: string;
  deviceGroupUid: string;
  displayDeviceState: string;
  displayDeviceUid: string;
  eventId: string;
  fieldUid: string;
  message: WsBaseMessage;
  parentDeviceState: string;
  receivedAt: string;
  receivedAtMs: number;
  sentAtMs: number;
  sequence: number;
  sourceMessageKey: string;
  sourceTraceId: string;
  sourceDeviceState: string;
  sourceDeviceUid: string;
}

export interface BoardRealtimeHttpSnapshot<TData> {
  data: TData;
  requestStartedAtMs: number;
}

export const BOARD_REALTIME_EVENT_HISTORY_LIMIT = 256;
const BOARD_DEVICE_REALTIME_PROJECTION_LIMIT = 1024;

export type BoardDeviceRealtimeProjectionState = Map<string, BoardDeviceRealtimeEvent>;

export function createBoardDeviceRealtimeProjectionState(): BoardDeviceRealtimeProjectionState {
  return new Map();
}

/** 장비·이벤트 계열별 최신 상태만 bounded projection으로 유지합니다. */
export function reduceBoardDeviceRealtimeProjection(latestByStreamKey: BoardDeviceRealtimeProjectionState, event: BoardDeviceRealtimeEvent): readonly BoardDeviceRealtimeEvent[] {
  const eventFamily = event.message.event === WsEventType.ISSUED_ASSET_STATUS ? WsEventType.ISSUED_ASSET_STATUS : 'SACON_DEVICE_STATE';
  const deviceUids = Array.from(new Set([event.sourceDeviceUid, event.displayDeviceUid].filter(Boolean)));
  const streamKeys = deviceUids.length > 0 ? deviceUids.map(deviceUid => `${event.fieldUid}:${eventFamily}:${deviceUid}`) : [event.eventId];
  streamKeys.forEach(streamKey => {
    latestByStreamKey.delete(streamKey);
    latestByStreamKey.set(streamKey, event);
  });
  while (latestByStreamKey.size > BOARD_DEVICE_REALTIME_PROJECTION_LIMIT) {
    const oldestStreamKey = latestByStreamKey.keys().next().value;
    if (oldestStreamKey === undefined) break;
    latestByStreamKey.delete(oldestStreamKey);
  }
  return [...new Map([...latestByStreamKey.values()].map(projectedEvent => [projectedEvent.eventId, projectedEvent])).values()];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readFirstString(records: Array<Record<string, unknown> | null>, keys: string[]): string {
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const value = readString(record[key]);
      if (value) return value;
    }
  }
  return '';
}

/** 지도와 터널 위젯이 공유할 수 있도록 장비 실시간 메시지의 식별 필드를 정규화합니다. */
export function normalizeBoardDeviceRealtimeEvent(message: WsBaseMessage, sequence: number): BoardDeviceRealtimeEvent | null {
  const body = asRecord(message.body);
  if (!body) return null;

  const detail = asRecord(body.detail);
  const data = asRecord(body.data);
  const records = [body, detail, data];
  const fieldUid = resolveMessageFieldUid(message) ?? readFirstString(records, ['field_uid', 'fieldUid']);
  if (!fieldUid) return null;

  const sourceDeviceUid = readFirstString(records, ['source_device_uid', 'sourceDeviceUid', 'original_device_uid', 'originalDeviceUid', 'device_uid', 'deviceUid']);
  const displayDeviceUid = readFirstString(records, ['display_device_uid', 'displayDeviceUid', 'device_uid', 'deviceUid']);
  const deviceGroupUid = readFirstString(records, ['device_group_uid', 'deviceGroupUid']);
  const displayDeviceState = readFirstString(records, ['device_state', 'deviceState']);
  const originalDeviceState = readFirstString(records, ['original_device_state', 'originalDeviceState', 'source_device_state', 'sourceDeviceState']);
  const sourceDeviceState = originalDeviceState || (sourceDeviceUid === displayDeviceUid ? displayDeviceState : '');
  const sourceTraceId = readFirstString(records, ['source_trace_id', 'sourceTraceId', 'trace_id', 'traceId']);
  const sourceMessageKey = readFirstString(records, ['source_message_key', 'sourceMessageKey', 'message_key', 'messageKey']);
  const correlationId = readFirstString(records, ['event_uid', 'eventUid', 'correlation_id', 'correlationId']) || sourceTraceId;

  const receivedAtMs = Date.now();
  const receivedAt = new Date(receivedAtMs).toISOString();
  const parsedSentAt = Date.parse(message.sent_at);

  return {
    correlationId,
    deviceGroupUid,
    displayDeviceState,
    displayDeviceUid,
    eventId: [message.type, message.event, message.key, message.sent_at].join(':'),
    fieldUid,
    message,
    parentDeviceState: readFirstString(records, ['parent_device_state', 'parentDeviceState']),
    receivedAt,
    receivedAtMs,
    sentAtMs: Number.isFinite(parsedSentAt) ? parsedSentAt : Date.parse(receivedAt),
    sequence,
    sourceMessageKey,
    sourceTraceId,
    sourceDeviceState,
    sourceDeviceUid,
  };
}

/** 최초 HTTP 요청 이후 수신된 증분만 snapshot 위에 순서대로 다시 적용합니다. */
export function reconcileBoardRealtimeHttpSnapshot<TData>(
  snapshot: BoardRealtimeHttpSnapshot<TData>,
  events: readonly BoardDeviceRealtimeEvent[],
  applyEvent: (data: TData, event: BoardDeviceRealtimeEvent) => TData
): TData {
  return events.reduce((data, event) => (event.receivedAtMs >= snapshot.requestStartedAtMs ? applyEvent(data, event) : data), snapshot.data);
}

/** 필드 전환마다 비워지는 짧은 이력으로 HTTP 요청 중 발생한 증분을 보존합니다. */
export function appendBoardRealtimeEvent<TEvent extends BoardDeviceRealtimeEvent>(history: readonly TEvent[], event: TEvent): readonly TEvent[] {
  if (history.some(candidate => candidate.eventId === event.eventId)) return history;
  const next = [...history, event];
  return next.length > BOARD_REALTIME_EVENT_HISTORY_LIMIT ? next.slice(-BOARD_REALTIME_EVENT_HISTORY_LIMIT) : next;
}
